/**
 * TRAD-3792 — 방문자가 «사전 동의(opt-in)»가 필요한 지역인지 판정한다.
 *
 * ── 왜 지역을 나누나 ─────────────────────────────────────────────────────────
 * TRAD-1155 배너(운영 2026-09-11)는 전 세계 방문자에게 동의를 받은 «뒤»에만 Firebase Analytics·
 * AdSense 를 켰다. 그 뒤로 GA web 신규가 0.74/일 → 0.07/일로 떨어졌다(GA 409625670 실측).
 * 사전 동의가 필요한 곳은 EU(EEA)·영국·스위스이고, 한국은 처리방침 고지 + 거부 방법으로 충분하다
 * (davidyoon 2026-10-22 «지역별 적용» 결정). 그래서 opt-in 지역만 지금 배너를 유지한다.
 *
 * ── 왜 브라우저 시간대인가 ───────────────────────────────────────────────────
 * 서버가 국가를 모른다. 2026-10-22 실측 기준으로 xtrading.kr 은 NAS 에 직결돼 있고(CDN 국가 헤더 없음),
 * nginx 에 GeoIP 도 없으며, 정적 프리렌더 HTML 이라 요청별로 값을 주입할 수도 없다.
 * 그래서 `Intl` 시간대(기기 설정)로 근사한다. 언어(navigator.language)는 쓰지 않는다 —
 * 영어·독일어 사용자가 전 세계에 있어 지역 신호가 안 된다.
 *
 * ── 🔴 이 판정이 못 잡는 것 (팀장 2026-10-22 수용, 근사임을 알고 쓴다) ─────────
 *  - 기기 시간대를 EU 밖(예: Asia/Seoul)으로 둔 EU 거주자 → opt-out 으로 판정돼 배너 없이 수집된다.
 *  - 유럽 여행 중이라 기기 시간대가 Europe/* 로 바뀐 한국인 → opt-in 으로 판정돼 배너가 뜬다(보수 쪽 오차).
 *  - VPN 은 시간대를 안 바꾸므로 IP 위치와는 무관하다(IP 판정이었다면 반대 오차가 났을 것).
 *
 * ── 실패하면 opt-in 이다 ─────────────────────────────────────────────────────
 * 판정이 안 되는 경우(Intl 없음·던짐·빈 값·`UTC`·`Etc/*`)는 전부 opt-in 이다. 개인정보 보호
 * 브라우저(Firefox resistFingerprinting·Tor)는 시간대를 UTC 로 보고하는데, 그 사용자가 어디 있는지
 * 모르므로 **물어보는 쪽으로 틀린다.**
 */

/** opt-in = 선택 전 추적 금지 + 배너로 묻는다 / opt-out = 선택 전에도 추적, 거부 수단만 제공. */
export type ConsentRegion = 'opt-in' | 'opt-out';

/**
 * `Europe/*` 접두가 아닌데 EEA·영국·스위스에 속하는 IANA 시간대.
 * (`Europe/*` 는 접두로 전부 opt-in — 러시아·우크라이나·터키처럼 EEA 밖도 보수적으로 포함한다.)
 *
 * 🔴 이 목록은 «정본 IANA 이름 + 흔한 별칭»이다. 브라우저는 보통 정본 이름을 돌려주지만 별칭(예: `Iceland`)을
 * 그대로 주는 엔진도 있어서 같이 넣었다. 여기 없는 해외영토 시간대는 opt-out 으로 판정된다 — 빠진 것을
 * 찾으면 추가할 것.
 */
export const OPT_IN_NON_EUROPE_TIME_ZONES: ReadonlySet<string> = new Set([
  // 스페인·포르투갈 대서양 영토
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  // 아이슬란드(EEA)·덴마크령 페로
  'Atlantic/Reykjavik',
  'Iceland',
  'Atlantic/Faroe',
  'Atlantic/Faeroe',
  // 노르웨이령 스발바르(EEA)
  'Arctic/Longyearbyen',
  'Atlantic/Jan_Mayen',
  // 키프로스(EU) — IANA 가 Asia/* 로 둔다
  'Asia/Nicosia',
  'Asia/Famagusta',
  // 스페인령 북아프리카(세우타·멜리야)
  'Africa/Ceuta',
  // 프랑스 해외 레지옹(EU 영역)
  'America/Guadeloupe',
  'America/Martinique',
  'America/Cayenne',
  'America/Marigot', // 생마르탱(프랑스령, EU 최외곽 지역)
  'Indian/Reunion',
  'Indian/Mayotte',
  // 영국·아일랜드 별칭(정본은 Europe/London·Europe/Dublin). 영국 왕실령·지브롤터도 Europe/* 라 접두로 잡힌다.
  'GB',
  'GB-Eire',
  'Eire',
  // 포르투갈·폴란드 별칭
  'Portugal',
  'Poland',
]);

/**
 * 시간대 문자열 → 지역. **순수 함수**다(같은 입력 → 같은 출력, 전역 접근 없음).
 *
 * @param timeZone `Intl.DateTimeFormat().resolvedOptions().timeZone` 값. 판정 불가면 `null`/`undefined`.
 */
export const resolveConsentRegion = (timeZone: string | null | undefined): ConsentRegion => {
  if (typeof timeZone !== 'string') return 'opt-in';
  const tz = timeZone.trim();
  if (tz === '') return 'opt-in';
  // 판정 불가 신호 — 어디 있는지 모르면 묻는다.
  if (tz === 'UTC' || tz === 'GMT' || tz === 'UCT' || tz === 'Universal' || tz === 'Zulu' || tz.startsWith('Etc/')) {
    return 'opt-in';
  }
  if (tz.startsWith('Europe/')) return 'opt-in';
  if (OPT_IN_NON_EUROPE_TIME_ZONES.has(tz)) return 'opt-in';
  return 'opt-out';
};

/**
 * 이 브라우저의 시간대를 읽는다 — **이 파일에서 유일하게 환경에 기대는 자리**.
 * 읽을 수 없으면 `null`(→ `resolveConsentRegion` 이 opt-in 으로 폴백).
 */
export const detectTimeZone = (): string | null => {
  try {
    if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return null;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof tz === 'string' && tz !== '' ? tz : null;
  } catch (e) {
    // raw error 를 삼키지 않는다 — opt-in 폴백은 안전하지만, 왜 모든 방문자에게 배너가 뜨는지는 추적할 수 있어야 한다.
    console.error('[consent-region] failed to read time zone — falling back to opt-in', e);
    return null;
  }
};

/** 지금 이 방문자의 지역. 공개 API — 전역(Intl)을 쓴다. */
export const currentConsentRegion = (): ConsentRegion => resolveConsentRegion(detectTimeZone());
